import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import Swal from "sweetalert2";
import PageTitle from "../../layouts/PageTitle";
import {
  deleteReservation,
  fetchReservations,
  updateReservationArchive,
} from "../../../services/ReservationService";
import "./Reservations.css";

const PAGE_SIZE_OPTIONS = [8, 12, 24, 50];
const DEFAULT_PAGE_SIZE = 12;
const ARCHIVED_PAGE_BODY_CLASS = "reservation-archived-table-page-active";

const RESERVATION_STATUS_OPTIONS = [
  "pending",
  "confirmed",
  "arrived",
  "completed",
  "cancelled",
  "no-show",
];

const SORT_OPTIONS = [
  { value: "archivedAt_desc", labelKey: "reservationArchivedSortArchivedNewest" },
  { value: "archivedAt_asc", labelKey: "reservationArchivedSortArchivedOldest" },
  { value: "startAt_desc", labelKey: "reservationArchivedSortDateNewest" },
  { value: "startAt_asc", labelKey: "reservationArchivedSortDateOldest" },
  { value: "customerName_asc", labelKey: "reservationArchivedSortNameAsc" },
  { value: "partySize_desc", labelKey: "reservationArchivedSortGuestsDesc" },
];

const getGuestInitials = (name = "") => {
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
};

const getReservationId = (reservation) =>
  String(reservation?._id || reservation?.id || "").trim();

const getApiErrorMessage = (error, fallback) =>
  error?.response?.data?.message || error?.message || fallback;

const getTimestamp = (value) => {
  if (!value) return 0;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? 0 : date.getTime();
};

const sortReservations = (list, sortBy) => {
  const items = [...list];
  switch (sortBy) {
    case "archivedAt_asc":
      return items.sort(
        (a, b) => getTimestamp(a.archivedAt) - getTimestamp(b.archivedAt)
      );
    case "startAt_desc":
      return items.sort(
        (a, b) => getTimestamp(b.startAt) - getTimestamp(a.startAt)
      );
    case "startAt_asc":
      return items.sort(
        (a, b) => getTimestamp(a.startAt) - getTimestamp(b.startAt)
      );
    case "customerName_asc":
      return items.sort((a, b) =>
        String(a.customerName || "").localeCompare(String(b.customerName || ""), undefined, {
          sensitivity: "base",
        })
      );
    case "partySize_desc":
      return items.sort(
        (a, b) => Number(b.partySize || 0) - Number(a.partySize || 0)
      );
    case "archivedAt_desc":
    default:
      return items.sort(
        (a, b) => getTimestamp(b.archivedAt) - getTimestamp(a.archivedAt)
      );
  }
};

const getVisiblePages = (currentPage, totalPages) => {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, index) => index + 1);
  }

  const pages = [1];
  const start = Math.max(2, currentPage - 1);
  const end = Math.min(totalPages - 1, currentPage + 1);

  if (start > 2) pages.push("ellipsis-start");
  for (let page = start; page <= end; page += 1) pages.push(page);
  if (end < totalPages - 1) pages.push("ellipsis-end");
  pages.push(totalPages);

  return pages;
};

const ArchivedTableReservations = () => {
  const { t } = useTranslation();
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [sourceFilter, setSourceFilter] = useState("");
  const [sortBy, setSortBy] = useState("archivedAt_desc");
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [page, setPage] = useState(1);
  const [busyId, setBusyId] = useState("");

  useEffect(() => {
    document.body.classList.add(ARCHIVED_PAGE_BODY_CLASS);
    return () => document.body.classList.remove(ARCHIVED_PAGE_BODY_CLASS);
  }, []);

  const loadArchived = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchReservations({ archived: true });
      setReservations(Array.isArray(data) ? data : []);
    } catch (error) {
      toast.error(getApiErrorMessage(error, t("reservationLoadError")));
      setReservations([]);
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadArchived();
  }, [loadArchived]);

  useEffect(() => {
    setPage(1);
  }, [search, statusFilter, sourceFilter, sortBy, pageSize]);

  const hasActiveFilters = Boolean(
    search.trim() || statusFilter || sourceFilter
  );

  const filteredReservations = useMemo(() => {
    const term = search.trim().toLowerCase();
    return reservations.filter((reservation) => {
      if (statusFilter && reservation.status !== statusFilter) return false;
      if (sourceFilter && reservation.source !== sourceFilter) return false;

      if (!term) return true;

      const haystack = [
        reservation.customerName,
        reservation.customerPhone,
        reservation.customerEmail,
        reservation.table?.name,
        reservation.tableNumber,
        reservation.status,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(term);
    });
  }, [reservations, search, statusFilter, sourceFilter]);

  const sortedReservations = useMemo(
    () => sortReservations(filteredReservations, sortBy),
    [filteredReservations, sortBy]
  );

  const totalPages = Math.max(1, Math.ceil(sortedReservations.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const paginatedReservations = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return sortedReservations.slice(start, start + pageSize);
  }, [sortedReservations, currentPage, pageSize]);

  const visiblePages = useMemo(
    () => getVisiblePages(currentPage, totalPages),
    [currentPage, totalPages]
  );

  const showingFrom =
    sortedReservations.length === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const showingTo = Math.min(currentPage * pageSize, sortedReservations.length);

  const clearFilters = () => {
    setSearch("");
    setStatusFilter("");
    setSourceFilter("");
  };

  const handleRestore = async (reservation) => {
    const reservationId = getReservationId(reservation);
    if (!reservationId || busyId) return;

    const result = await Swal.fire({
      title: t("reservationRestoreConfirm"),
      text: t("reservationRestoreConfirmText"),
      icon: "question",
      showCancelButton: true,
      confirmButtonText: t("reservationRestoreAction"),
      cancelButtonText: t("cancel"),
      confirmButtonColor: "#2563eb",
      cancelButtonColor: "#94a3b8",
    });

    if (!result.isConfirmed) return;

    setBusyId(reservationId);
    try {
      await updateReservationArchive(reservationId, false);
      setReservations((prev) =>
        prev.filter((item) => getReservationId(item) !== reservationId)
      );
      toast.success(t("reservationRestored"));
    } catch (error) {
      toast.error(getApiErrorMessage(error, t("reservationUpdateError")));
    } finally {
      setBusyId("");
    }
  };

  const handleDelete = async (reservation) => {
    const reservationId = getReservationId(reservation);
    if (!reservationId || busyId) return;

    const result = await Swal.fire({
      title: t("reservationDeleteConfirmTitle"),
      text: t("reservationDeleteConfirm"),
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: t("delete"),
      cancelButtonText: t("cancel"),
      confirmButtonColor: "#dc2626",
      cancelButtonColor: "#94a3b8",
    });

    if (!result.isConfirmed) return;

    setBusyId(reservationId);
    try {
      await deleteReservation(reservationId);
      setReservations((prev) =>
        prev.filter((item) => getReservationId(item) !== reservationId)
      );
      toast.success(t("reservationDeleted"));
    } catch (error) {
      toast.error(getApiErrorMessage(error, t("reservationDeleteError")));
    } finally {
      setBusyId("");
    }
  };

  return (
    <Fragment>
      <PageTitle
        motherMenu={t("Archived")}
        activeMenu={t("navArchivedReservations")}
      />

      <div className="reservation-archived-page">
        <div className="reservation-archived-hero">
          <div className="reservation-archived-hero__main">
            <span className="reservation-archived-hero__eyebrow">{t("Archived")}</span>
            <h1>{t("reservationArchivedPageTitle")}</h1>
            <p>{t("reservationArchivedPageSubtitle")}</p>
          </div>
          <Link to="/reservations" className="reservation-archived-hero__back">
            <i className="fa fa-arrow-left" aria-hidden="true" />
            {t("reservationArchivedBackToReservations")}
          </Link>
        </div>

        <div className="reservation-list-card card reservation-archived-card">
          <div className="card-body">
            <div className="reservation-archived-card-head">
              <div>
                <span>{t("navArchivedReservations")}</span>
                <h4>{t("reservationArchivedPageTitle")}</h4>
              </div>
              <span className="reservation-list-count">{sortedReservations.length}</span>
            </div>

            <div className="reservation-archived-toolbar">
              <div className="reservation-archived-search">
                <i className="fa fa-search" aria-hidden="true" />
                <input
                  type="search"
                  className="form-control"
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder={t("reservationArchivedSearchPlaceholder")}
                />
              </div>

              <select
                className="reservation-archived-select"
                value={statusFilter}
                onChange={(event) => setStatusFilter(event.target.value)}
                aria-label={t("reservationArchivedFilterStatus")}
              >
                <option value="">{t("reservationArchivedFilterAllStatus")}</option>
                {RESERVATION_STATUS_OPTIONS.map((status) => (
                  <option key={status} value={status}>
                    {t(`reservationStatus_${status}`)}
                  </option>
                ))}
              </select>

              <select
                className="reservation-archived-select"
                value={sourceFilter}
                onChange={(event) => setSourceFilter(event.target.value)}
                aria-label={t("reservationArchivedFilterSource")}
              >
                <option value="">{t("reservationArchivedFilterAllSources")}</option>
                <option value="client">{t("reservationSourceClient")}</option>
                <option value="admin">{t("reservationSourceAdmin")}</option>
              </select>

              <select
                className="reservation-archived-select"
                value={sortBy}
                onChange={(event) => setSortBy(event.target.value)}
                aria-label={t("reservationArchivedSortBy")}
              >
                {SORT_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {t(option.labelKey)}
                  </option>
                ))}
              </select>

              {hasActiveFilters ? (
                <button
                  type="button"
                  className="reservation-archived-clear-btn"
                  onClick={clearFilters}
                >
                  <i className="fa fa-times" aria-hidden="true" />
                  {t("reservationArchivedClearFilters")}
                </button>
              ) : null}
            </div>

            {loading ? (
              <div className="reservation-list-empty">
                <i className="fa fa-spinner fa-spin" />
                <span>{t("loading")}…</span>
              </div>
            ) : reservations.length === 0 ? (
              <div className="reservation-list-empty">
                <i className="fa fa-archive" />
                <strong>{t("reservationArchivedEmpty")}</strong>
              </div>
            ) : sortedReservations.length === 0 ? (
              <div className="reservation-list-empty">
                <i className="fa fa-filter" />
                <strong>{t("reservationArchivedFilterEmpty")}</strong>
              </div>
            ) : (
              <>
                <div className="reservation-archived-table-card">
                  <div className="reservation-archived-table-wrap">
                    <table className="reservation-archived-table mb-0">
                    <thead>
                      <tr>
                        <th>
                          <span className="reservation-th">
                            <i className="fa fa-user" />
                            {t("reservationCustomer")}
                          </span>
                        </th>
                        <th>
                          <span className="reservation-th">
                            <i className="fa fa-phone" />
                            {t("reservationFormPhone")}
                          </span>
                        </th>
                        <th>
                          <span className="reservation-th">
                            <i className="fa fa-calendar" />
                            {t("reservationDate")}
                          </span>
                        </th>
                        <th>
                          <span className="reservation-th">
                            <i className="fa fa-users" />
                            {t("reservationPartySize")}
                          </span>
                        </th>
                        <th>
                          <span className="reservation-th">
                            <i className="fas fa-chair" />
                            {t("reservationTable")}
                          </span>
                        </th>
                        <th>
                          <span className="reservation-th">
                            <i className="fa fa-check-circle" />
                            {t("status")}
                          </span>
                        </th>
                        <th>
                          <span className="reservation-th">
                            <i className="fa fa-archive" />
                            {t("reservationArchivedAt")}
                          </span>
                        </th>
                        <th className="reservation-archived-actions-head">
                          <span className="reservation-archived-th reservation-archived-th--end">
                            <i className="fa fa-ellipsis-h" />
                            {t("actions")}
                          </span>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedReservations.map((reservation) => {
                        const reservationId = getReservationId(reservation);
                        const start = reservation.startAt
                          ? new Date(reservation.startAt)
                          : null;
                        const validStart = start && !Number.isNaN(start.getTime());
                        const tableLabel =
                          reservation.table?.name ||
                          (reservation.tableNumber
                            ? `${t("reservationTable")} ${reservation.tableNumber}`
                            : t("reservationTableUnassigned"));
                        const archivedAt = reservation.archivedAt
                          ? new Date(reservation.archivedAt)
                          : null;
                        const validArchivedAt =
                          archivedAt && !Number.isNaN(archivedAt.getTime());
                        const unassigned =
                          !reservation.table && !reservation.tableNumber;
                        const busy = busyId === reservationId;

                        return (
                          <tr key={reservationId || reservation.customerName}>
                            <td>
                              <div className="reservation-td-guest">
                                <span className="reservation-td-avatar" aria-hidden="true">
                                  {getGuestInitials(reservation.customerName)}
                                </span>
                                <div className="reservation-td-guest-block">
                                  <strong className="reservation-td-name">
                                    {reservation.customerName || "—"}
                                  </strong>
                                  {reservation.customerEmail ? (
                                    <span
                                      className="reservation-td-email"
                                      title={reservation.customerEmail}
                                    >
                                      {reservation.customerEmail}
                                    </span>
                                  ) : (
                                    <span className="reservation-td-email is-empty">—</span>
                                  )}
                                </div>
                              </div>
                            </td>
                            <td>
                              <span className="reservation-td-phone">
                                <i className="fa fa-phone" />
                                {reservation.customerPhone || "—"}
                              </span>
                            </td>
                            <td>
                              <div className="reservation-td-when">
                                <strong className="reservation-td-date">
                                  {validStart
                                    ? start.toLocaleDateString([], {
                                        weekday: "short",
                                        day: "numeric",
                                        month: "short",
                                        year: "numeric",
                                      })
                                    : "—"}
                                </strong>
                                <span className="reservation-td-time">
                                  <i className="fa fa-clock-o" />
                                  {validStart
                                    ? start.toLocaleTimeString([], {
                                        hour: "2-digit",
                                        minute: "2-digit",
                                      })
                                    : "—"}
                                </span>
                              </div>
                            </td>
                            <td>
                              <span className="reservation-td-pill is-guests">
                                <i className="fa fa-users" />
                                {reservation.partySize || "—"}
                              </span>
                            </td>
                            <td>
                              <span
                                className={`reservation-td-pill${
                                  unassigned ? " is-warn" : " is-table"
                                }`}
                              >
                                <i className="fas fa-chair" />
                                {tableLabel}
                              </span>
                            </td>
                            <td>
                              <span
                                className={`reservation-td-status is-${reservation.status}`}
                              >
                                <i className="fa fa-circle" />
                                {t(`reservationStatus_${reservation.status}`)}
                              </span>
                            </td>
                            <td>
                              <span className="reservation-archived-at">
                                <i className="fa fa-archive" />
                                {validArchivedAt
                                  ? archivedAt.toLocaleString([], {
                                      day: "numeric",
                                      month: "short",
                                      year: "numeric",
                                      hour: "2-digit",
                                      minute: "2-digit",
                                    })
                                  : "—"}
                              </span>
                            </td>
                            <td className="reservation-archived-actions-cell">
                              <div className="reservation-archived-row-actions">
                                <button
                                  type="button"
                                  className="reservation-archived-icon-btn is-restore"
                                  disabled={busy}
                                  title={t("reservationRestoreAction")}
                                  aria-label={t("reservationRestoreAction")}
                                  onClick={() => handleRestore(reservation)}
                                >
                                  <i className="fa fa-undo" aria-hidden="true" />
                                </button>
                                <button
                                  type="button"
                                  className="reservation-archived-icon-btn is-delete"
                                  disabled={busy}
                                  title={t("delete")}
                                  aria-label={t("delete")}
                                  onClick={() => handleDelete(reservation)}
                                >
                                  <i className="fa fa-trash" aria-hidden="true" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  </div>

                  <div className="reservation-archived-pagination">
                  <div className="reservation-archived-pagination__meta">
                    <span>
                      {t("reservationArchivedShowingCount", {
                        from: showingFrom,
                        to: showingTo,
                        total: sortedReservations.length,
                      })}
                    </span>
                    <label className="reservation-archived-page-size">
                      <span>{t("reservationArchivedPerPage")}</span>
                      <select
                        value={pageSize}
                        onChange={(event) =>
                          setPageSize(Number(event.target.value))
                        }
                        aria-label={t("reservationArchivedPerPage")}
                      >
                        {PAGE_SIZE_OPTIONS.map((size) => (
                          <option key={size} value={size}>
                            {size}
                          </option>
                        ))}
                      </select>
                    </label>
                  </div>

                  {totalPages > 1 ? (
                    <div className="reservation-archived-pagination__btns">
                      <button
                        type="button"
                        className="reservation-archived-page-btn"
                        disabled={currentPage <= 1}
                        onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                        aria-label={t("previous")}
                      >
                        <i className="fa fa-chevron-left" aria-hidden="true" />
                      </button>

                      {visiblePages.map((pageNumber) =>
                        typeof pageNumber === "string" ? (
                          <span
                            key={pageNumber}
                            className="reservation-archived-page-ellipsis"
                            aria-hidden="true"
                          >
                            …
                          </span>
                        ) : (
                          <button
                            key={pageNumber}
                            type="button"
                            className={`reservation-archived-page-btn${
                              currentPage === pageNumber ? " is-active" : ""
                            }`}
                            onClick={() => setPage(pageNumber)}
                          >
                            {pageNumber}
                          </button>
                        )
                      )}

                      <button
                        type="button"
                        className="reservation-archived-page-btn"
                        disabled={currentPage >= totalPages}
                        onClick={() =>
                          setPage((prev) => Math.min(totalPages, prev + 1))
                        }
                        aria-label={t("next")}
                      >
                        <i className="fa fa-chevron-right" aria-hidden="true" />
                      </button>
                    </div>
                  ) : null}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </Fragment>
  );
};

export default ArchivedTableReservations;
